/**
 * Staging for the SKU material lists open inside a form — Max, 2026-09-03:
 * "KHI add thì chưa lưu nền sẽ dc lưu chung toàn model nhé."
 *
 * The material list is not part of the group PUT — it has its own table and its own endpoint — but
 * it must save when the form's own Save button is pressed, and only then. So the typing has to live
 * somewhere that outlives a collapsed SKU card (the group modal unmounts the editor when the card is
 * closed) and dies with the modal, so that Cancel throws it away like every other field on the form.
 *
 * One store per form. `save()` is called by that form's save handler.
 */
import { useMemo, useRef } from 'react';
import { BACKEND_URL } from './backendConfig';

export const MATERIAL_API = `${BACKEND_URL}/api/development/project-tracker/sku-materials`;

export function useSkuMaterialDrafts() {
  const staged = useRef(new Map());   // sku id -> rows as typed

  return useMemo(() => ({
    get: (skuId) => staged.current.get(skuId),
    put: (skuId, rows) => { staged.current.set(skuId, rows); },
    reset: () => { staged.current.clear(); },
    dirty: () => staged.current.size > 0,

    /* Writes every list the form is holding. One at a time and stopping at the first failure, the
       way the form already saves its SKUs: a half-written list is easier to reason about than a
       scatter of parallel errors, and the caller shows the message. A list that went through is
       dropped from the store, so pressing Save again after fixing the error does not rewrite it. */
    save: async () => {
      const entries = [...staged.current.entries()];
      for (const [skuId, rows] of entries) {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(`${MATERIAL_API}/${encodeURIComponent(skuId)}`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ materials: rows }),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `${skuId}: could not save the material list`);
        staged.current.delete(skuId);
      }
      return entries.length;
    },
  }), []);
}

export default useSkuMaterialDrafts;
